/**
 * Help Center search box. `hero` is the big box on the home page (results
 * render below it, from the parent); `compact` sits in the top bar and opens
 * a dropdown of the best matches. Arrow keys move, Enter opens, Escape closes.
 */
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHelpSearch } from './useHelpSearch';

export function SearchResultList({ results, activeIndex = -1, onPick, dense = false }) {
  return (
    <ul role="listbox" className={dense ? 'py-1' : 'divide-y divide-white/[0.06]'}>
      {results.map(({ article, snippet }, i) => (
        <li key={article.id} role="option" aria-selected={i === activeIndex}>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(article)}
            className={cn(
              'block w-full text-left transition-colors',
              dense ? 'px-4 py-2.5 rounded-xl' : 'px-1 py-4',
              i === activeIndex ? 'bg-white/[0.06]' : 'hover:bg-white/[0.04]',
            )}
          >
            <span className="block text-[11.5px] uppercase tracking-[0.14em] text-white/45">{article.sectionTitle}</span>
            <span className={cn('block text-white font-medium', dense ? 'text-[14.5px] mt-0.5' : 'text-[16.5px] mt-1')}>{article.title}</span>
            {!dense && snippet && <span className="mt-1 block text-[14px] leading-relaxed text-white/60">{snippet}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}
SearchResultList.propTypes = {
  results: PropTypes.array.isRequired,
  activeIndex: PropTypes.number,
  onPick: PropTypes.func.isRequired,
  dense: PropTypes.bool,
};

export default function HelpSearch({ variant = 'compact', value, onChange, autoFocus = false, placeholder }) {
  const navigate = useNavigate();
  const [internal, setInternal] = useState('');
  const query = value ?? internal;
  const setQuery = onChange ?? setInternal;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);
  const results = useHelpSearch(query, variant === 'compact' ? 8 : 20);
  const hero = variant === 'hero';
  // The hero's example queries don't fit a phone-width box.
  const [wide, setWide] = useState(() => typeof window === 'undefined' || !window.matchMedia || window.matchMedia('(min-width: 640px)').matches);
  useEffect(() => {
    if (!hero || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 640px)');
    const onChange = () => setWide(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [hero]);

  useEffect(() => { setActive(-1); }, [query]);
  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);

  const pick = (article) => {
    setOpen(false);
    if (!hero) setQuery('');
    navigate(article.path);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); return; }
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(results.length - 1, a + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(-1, a - 1)); }
    if (e.key === 'Enter') {
      e.preventDefault();
      pick(results[Math.max(0, active)].article);
    }
  };

  return (
    <div className="relative w-full">
      <div className={cn(
        'flex items-center gap-3 rounded-full border transition-colors',
        hero
          ? 'h-14 sm:h-16 px-5 sm:px-6 border-white/15 bg-white/[0.06] focus-within:border-[#7A8ABF]/60 focus-within:bg-white/[0.08] shadow-[0_20px_60px_-20px_rgba(15,25,82,0.9)]'
          : 'h-10 px-4 border-white/10 bg-white/[0.04] focus-within:border-white/25',
      )}>
        <Search className={cn('flex-shrink-0 text-white/45', hero ? 'h-5 w-5' : 'h-4 w-4')} strokeWidth={1.75} aria-hidden />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={!hero && open && results.length > 0}
          aria-label="Search the Help Center"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || (hero ? (wide ? 'Search for answers: “change my flight”, “confirmation number”…' : 'Search for answers') : 'Search help')}
          className={cn(
            'min-w-0 flex-1 bg-transparent text-white placeholder:text-white/35 focus:outline-none [&::-webkit-search-cancel-button]:hidden',
            hero ? 'text-base sm:text-[17px]' : 'text-base md:text-[14px]',
          )}
        />
        {query && (
          <button type="button" onClick={() => { setQuery(''); inputRef.current?.focus(); }} aria-label="Clear search" className="text-white/40 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {!hero && open && query.trim() && (
        <div className="absolute left-0 right-0 top-full mt-2 z-50 rounded-2xl border border-white/10 bg-[#0b0d16]/95 backdrop-blur-xl shadow-2xl p-1.5 max-h-[70vh] overflow-y-auto">
          {results.length
            ? <SearchResultList results={results} activeIndex={active} onPick={pick} dense />
            : <p className="px-4 py-3 text-[14px] text-white/55">No articles match “{query.trim()}”. Try fewer words.</p>}
        </div>
      )}
    </div>
  );
}
HelpSearch.propTypes = {
  variant: PropTypes.oneOf(['hero', 'compact']),
  value: PropTypes.string,
  onChange: PropTypes.func,
  autoFocus: PropTypes.bool,
  placeholder: PropTypes.string,
};
